
import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Skeleton, Typography } from '@mui/material';
import { CanceledError } from 'axios';
import ListItemIcon from '@mui/material/ListItemIcon';
import EditIcon from '@mui/icons-material/Edit';
import { RootState } from '@app/store';
import { Document, DocumentSearch, DocumentSearchSchema } from '../../../models/document';
import { DocumentStructure } from '../../../models/document-structure';
import { setDocumentsSearch } from '../store/browse.slice';
import { getDocuments } from '../api/documents.service';
import { deleteEmptyFieldsFromObject, setAllValuesToNull } from '@shared/lib/utils';
// import { setDocumentsStructure } from '@features/structures/store/structure.slice';
import DataTable from '@shared/ui/data-table/DataTable';
import { StyledBrowsePage, getLastAvailableLink, useEntityBrowse } from "@features/tickets/public";
import { SubmitHandler, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
// import { DevTool } from '@hookform/devtools';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTheme } from "@emotion/react";
import { useErrorBoundary } from "react-error-boundary";
import { handleErrorWithBoundary } from "@shared/lib/errors"
import AddDocumentMenu from './add-document-menu/AddDocumentMenu';
import { BreadCrumbData } from '@shared/lib/navigation/Breadcrumbs.types';
import ReturnButton from '@shared/ui/navigation/ReturnButton';
import { useGetDocumentsStructureQuery } from '@features/structures/api/documentsStructure.api';

//state properties:

interface LocationState {

  from?: string;
  method?: string;
  document_settings_id?: number;
  searchFormData?: DocumentSearch;

  breadcrumbData?: BreadCrumbData[];

}

function Documents() {

  const { showBoundary } = useErrorBoundary();
  const dispatch = useDispatch();
  const { state } = useLocation() as { state: LocationState };
  const navigate = useNavigate();
  const theme = useTheme();


  const { data: documentsStructure } = useGetDocumentsStructureQuery();

  // const documentStructure = useSelector((state: RootState) => state.structure.document);
  const documentsSearch = useSelector((state: RootState) => state.browse.documentsSearch);

  const [data, setData] = useState<Document[]>([]);
  const [tableStructure, setTableStructure] = useState<DocumentStructure[]>();
  const [sortKey, setSortKey] = useState<string>('id');
  const [sortMethod, setSortMethod] = useState<string>('DESC');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const documentSearchForm = useForm<DocumentSearch>({
    resolver: zodResolver(DocumentSearchSchema),
    defaultValues: {
      ...documentsSearch,
    }
  });

  const options = [
    {
      label: "עריכה",
      icon: <ListItemIcon sx={{ color: theme.colors.main_color }}><EditIcon fontSize="small" /></ListItemIcon>,
      href: ""
    },
  ];


  // get documents structure
  // useEffect(() => {
  //   if (!documentStructure) {
  //     getStructureAndSaveAtStore();
  //   }
  // }, []);

  //set the structure fields shown in the table
  useEffect(() => {
    setTableStructure(documentsStructure?.filter(field => field.show_browse))
  }, [documentsStructure]);

  function handleSortKeyChanged(value: string) {
    setSortKey(value);
  }

  function handleSortMethodChanged(value: string) {
    setSortMethod(value);
  }

  async function getData(data: DocumentSearch) {

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    setIsLoading(true);

    if(state?.searchFormData){
      data = state.searchFormData;
    }


    try {
      const responseData = await getDocuments(sortKey || 'id', "10", sortMethod || 'ASC', ["documentSettings", "documentAccount.customData", "car.customData", "works.customData", "items.customData", "payments", "account"], deleteEmptyFieldsFromObject(data), abortControllerRef.current.signal);
      setData(responseData || []);
      setIsLoading(false);
    } catch (error: unknown) {
      if (error instanceof CanceledError) {
        return;
      }
      handleErrorWithBoundary(error, showBoundary);
    }

  }

  const onSearch: SubmitHandler<DocumentSearch> = (data) => {

    dispatch(setDocumentsSearch(deleteEmptyFieldsFromObject(data)));
    getData(deleteEmptyFieldsFromObject(data));
  };

  useEntityBrowse({
    form: documentSearchForm,
    sortKey,
    sortMethod,
    onSearch
  });


  function rowChosen<T extends Document>(row: T) {

    if(state?.breadcrumbData){
      if (state?.method === "browse document") {
        navigate(getLastAvailableLink(state.breadcrumbData), {
          replace: true,
          state: {
            // ...location.state,
            method: 'browse document',
            document_settings_id: state?.document_settings_id,
            // document: row,
            document_account: {...row.document_account, id: undefined, account_id: row.document_account?.account_id},
            car: {...row.car, id: undefined, car_id: row.car?.car_id},
            works: row.works,
            items: row.items,
            reference: null,

            breadcrumbData: state.breadcrumbData.slice(0,-1)
          }
        });
      }

      if (state?.method === "reference") {
        navigate(getLastAvailableLink(state.breadcrumbData), {
          replace: true,
          state: {
            // ...state,
            method: 'reference',
            document_settings_id: state?.document_settings_id,
            // document: row,
            document_account: {...row.document_account, id: undefined, account_id: row.document_account?.account_id},
            car: {...row.car, id: undefined, car_id: row.car?.car_id},
            works: row.works,
            items: row.items,
            reference_id: row.id,
            reference: `${row.document_settings?.name} ${row.document_number}`,
            searchFormData: null,

            breadcrumbData: state.breadcrumbData.slice(0,-1)
          }
        });
      }
    }    
  }

  function editBrowseStructure() {
    navigate('/documents/structure/browse', {
      replace: true, 
      
      state: {
        from: location.pathname,
        fromType: 'browse',
        structureName: "מסמך",
        structureType: 'document',
        documentType: 'מסמך',

        breadcrumbData: [
          {
              name: "מסמכים",
              link: `/documents`
          },
          {
              name: "הגדרת מבנה דפדוף מסמכים"
          }
        ]
      }
    });
  }

  function reset() {

    const values = setAllValuesToNull(documentSearchForm.getValues());
    values.date_time = null;

    // for (const key in values) {
    //   if (values.hasOwnProperty(key)) {
    //     documentSearchForm.setValue(key as keyof DocumentSearch, values[key as keyof DocumentSearch]);
    //   }
    // }

    documentSearchForm.reset(values);
  }

  return (

    <StyledBrowsePage>

      <div>
        <Typography variant="h5" component="span">
          דפדוף במסמכים
        </Typography>

        {/* <button onClick={addDocument}>+ הוספת מסמך</button> */}
        {/* <Tooltip title="הוספת מסמך">
          <IconButton onClick={addDocument} sx={{ color: theme.colors.main_color }}>
            <NoteAddIcon />
          </IconButton>
        </Tooltip> */}

        <AddDocumentMenu></AddDocumentMenu>
      </div>


      {tableStructure ?
        <DataTable structure={[...tableStructure]} setStructure={editBrowseStructure} searchForm={documentSearchForm} onResetSearch={reset} sortKey={sortKey} setSortKey={handleSortKeyChanged} sortMethod={sortMethod} setSortMethod={handleSortMethodChanged} data={data} isLoading={isLoading} options={options}  selectRow={state?.breadcrumbData ? true : false} onSelectRow={rowChosen} /> :
        <Skeleton variant="rectangular" height={150} animation="wave" sx={{ bgcolor: theme.colors.background_color }} />}
      <ReturnButton/>
      {/* <DevTool control={documentSearchForm.control} /> */}


    </StyledBrowsePage>
  );
}

export default Documents;